import {DATA_TYPE_NAMES} from "../components/dashboard/widgets/widget-constants";
import dashboardService from "../services/dashboard-service";

const getRedBackgroundColors = () => ["#AF1003", "#EC3323", "#FF6053", "#FF857B"];
const getGreenBackgroundColors = () => ["#20B206", "#72B566", "#66B5B0", "#3CC8D1"];
const getOtherBackgroundColors = () => [
    "#F3C85C",
    "#6F81DF",
    "#E64E7C",
    "#F4A83B",
    "#9766C9",
    "#29ABE2",
    "#E74EB3",
    "#1738E8",
    "#6D08D5",
    "#C27405",
];

const NEGATIVE_LABELS = [
    "unsuccessful",
    "changed",
    "failed",
    "missed",
    "skipped",
    "error",
    "validation_error",
    "validation_canceled",
    "unreachable",
    "aborted",
    "auth_failure",
    "not_installed",
    "offline",
    "disabled",
    "invalid",
    "canceled",
    "validation error",
];
const POSITIVE_LABELS = [
    "successful",
    "success",
    "unchanged",
    "validation_success",
    "discovered",
    "passed",
    "ok",
    "in_sync",
    "ready",
    "complete",
    "validated",
    "scheduled",
    "passed",
];

const hexToHsl = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) {
        return [0, 0, l];
    }
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === r) {
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    } else if (max === g) {
        h = ((b - r) / d + 2) / 6;
    } else {
        h = ((r - g) / d + 4) / 6;
    }
    return [h * 360, s, l];
};

const adjustColor = (hex, cycle) => {
    if (cycle === 0) {
        return hex;
    }
    const [h, s, l] = hexToHsl(hex);
    const hueShift = cycle * 30;
    const lightnessShift = (cycle % 2 === 1 ? -0.08 : 0.08) * Math.ceil(cycle / 2);
    const newH = (h + hueShift) % 360;
    const newS = Math.min(1, Math.max(0.2, s));
    const newL = Math.min(0.75, Math.max(0.25, l + lightnessShift));
    return `hsl(${Math.round(newH)}, ${Math.round(newS * 100)}%, ${Math.round(newL * 100)}%)`;
};

const getColorForLabel = (label, colors) => {
    let palette;
    if (NEGATIVE_LABELS.includes(label.toLowerCase())) {
        palette = colors.red;
    } else if (POSITIVE_LABELS.includes(label.toLowerCase())) {
        palette = colors.green;
    } else {
        palette = colors.other;
    }
    if (!palette._index) {
        palette._index = 0;
    }
    const cycle = Math.floor(palette._index / palette.length);
    const color = palette[palette._index % palette.length];
    palette._index++;
    return adjustColor(color, cycle);
};

export const formatDataChartData = (data, itemsSlice, dataType, _, dataTypeValue, dataProperty) => {
    const borderCol = "transparent";
    const labels = [];
    const dataValues = [];
    const colors = {
        red: getRedBackgroundColors(),
        green: getGreenBackgroundColors(),
        other: getOtherBackgroundColors(),
    };
    const backgroundColors = [];

    const chartData = Object.entries(data)
        .map(([label, value]) => ({label, value}))
        .sort((a, b) => b.value - a.value);

    const labelsDataset = dashboardService.getDataChartLabels(dataTypeValue, dataProperty);
    if (labelsDataset) {
        chartData.forEach((item) => {
            item.label = labelsDataset[item.label];
            if (item.label === undefined) {
                item.label = "Blank";
            }
        });
    }

    const DEFAULT_ITEMS_SLICE = 20;
    const sliceValue = itemsSlice?.value ?? DEFAULT_ITEMS_SLICE;

    if (sliceValue > 0 && chartData.length > sliceValue) {
        const slicedData = chartData.slice(0, sliceValue - 1);
        const slicedDataRemains = chartData.slice(sliceValue - 1);
        slicedData.forEach((item) => {
            labels.push(item.label);
            dataValues.push(item.value);
            backgroundColors.push(getColorForLabel(item.label, colors));
        });
        let count = 0;
        slicedDataRemains.forEach((item) => {
            count += item.value;
        });
        labels.push("Other");
        dataValues.push(count);
        backgroundColors.push(getColorForLabel("Other", colors));
    } else {
        chartData.forEach((item) => {
            labels.push(item.label);
            dataValues.push(item.value);
            backgroundColors.push(getColorForLabel(item.label, colors));
        });
    }

    return {
        labels,
        datasets: [
            {
                label: "test",
                backgroundColor: backgroundColors,
                data: dataValues,
                borderColor: borderCol,
            },
        ],
    };
};

export const generateLegendMarkup = (chart, singleLabelRedirect) => {
    const legendSet = chart.data.datasets[0];
    const legend = legendSet.data.map((set, i) => {
        const datapoint = chart.data.labels[i];
        if (datapoint) {
            return (
                <li
                    id={`${datapoint.replace(/\s/g, "")}`}
                    key={datapoint}
                    className="glu-chart__list-item"
                    data-idx={i}
                    data-quantity={legendSet.data[i]}
                    style={{borderColor: legendSet.backgroundColor[i]}}
                    onClick={singleLabelRedirect ? () => singleLabelRedirect(datapoint) : null}
                >
                    <div
                        style={{
                            border: `3px solid ${legendSet.backgroundColor[i]}`,
                        }}
                        className="glu-chart__legend-box glu-flex glu-flex-align-center"
                    >
                        <div
                            style={{backgroundColor: legendSet.backgroundColor[i]}}
                            className="glu-chart__box--inner"
                        />
                    </div>
                    {`${datapoint.replace(/\(\d{1, 6}\)$/g, "")}`}
                    <span className="glu-cahrt__legend-item-value">{legendSet.data[i]}</span>
                </li>
            );
        }
        return "";
    });
    return legend;
};

export const getSolutionLabel = (solution) => {
    let label = "";
    switch (solution) {
        case DATA_TYPE_NAMES.AUDIT_POLICIES:
            label = "Config Drift and Audit";
            break;
        case DATA_TYPE_NAMES.DEVICES:
            label = "Device Manager";
            break;
        case DATA_TYPE_NAMES.FILE_SERVERS:
            label = "File Servers";
            break;
        case DATA_TYPE_NAMES.FILES:
            label = "File Server";
            break;
        case DATA_TYPE_NAMES.OS_PLANS:
            label = "OS Manager";
            break;
        case DATA_TYPE_NAMES.SCHEDULES:
            label = "Schedules";
            break;
        default:
            return label;
    }

    return label;
};
